// SIP Header 名稱，後端據此解析 client metadata，屬於前後端契約，不可隨意更名
const CLIENT_META_HEADER_NAME = 'Kingnet-Client-Meta'
// Header 內容格式（欄位語意／解析規則）的版本；除非格式有變，否則固定維持 1，變更時才遞增
const CLIENT_META_SCHEMA_VERSION = '1'
// cid 的 localStorage 儲存 key；名稱與尾碼版本可視需要調整，版本號同樣依內容格式是否變更而定
const CLIENT_ID_STORAGE_KEY = 'cloudintercom:client-id:v1'
const UNKNOWN_CORRELATION_ID = 'unknown'
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/**
 * 產生只用於 log correlation 的 UUID v4；這些 ID 可以由 client 偽造，不能作為安全憑證。
 * randomUUID 在部分非安全 HTTP context 不可用，因此保留 getRandomValues fallback。
 */
const createCorrelationId = (cryptoObject) => {
  if (typeof cryptoObject?.randomUUID === 'function') {
    return cryptoObject.randomUUID()
  }

  const bytes = new Uint8Array(16)
  cryptoObject.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * crypto 不可用、呼叫失敗或未產生合法 UUID 時，明確回傳 unknown。
 * cid 與 sid 都必須經過此入口，避免 metadata 建立失敗中斷元件初始化。
 */
const createCorrelationIdOrUnknown = (cryptoObject) => {
  try {
    const correlationId = createCorrelationId(cryptoObject)
    return UUID_PATTERN.test(correlationId || '') ? correlationId : UNKNOWN_CORRELATION_ID
  } catch {
    return UNKNOWN_CORRELATION_ID
  }
}

/**
 * cid 代表同一 origin 下的瀏覽器 profile，而不是登入使用者或實體裝置。
 * localStorage 讓 cid 可跨重新整理、分頁與瀏覽器重啟保留；清除網站資料、無痕模式、
 * 更換瀏覽器時都可能產生新 cid。storage 無法可靠讀寫時固定回傳 unknown，
 * 避免將只能存在於本次頁面的暫時 ID 誤判為可跨頁面關聯的 cid。
 */
const getOrCreateClientId = ({ storageObject, cryptoObject }) => {
  if (typeof storageObject?.getItem !== 'function' || typeof storageObject?.setItem !== 'function') {
    return UNKNOWN_CORRELATION_ID
  }

  try {
    const storedClientId = storageObject.getItem(CLIENT_ID_STORAGE_KEY)

    if (UUID_PATTERN.test(storedClientId || '')) {
      return storedClientId
    }

    const clientId = createCorrelationIdOrUnknown(cryptoObject)

    if (clientId === UNKNOWN_CORRELATION_ID) {
      return UNKNOWN_CORRELATION_ID
    }

    storageObject.setItem(CLIENT_ID_STORAGE_KEY, clientId)

    // 寫入後回讀，只有確實存在 storage 的合法 UUID 才能宣告為可持久化 cid。
    const persistedClientId = storageObject.getItem(CLIENT_ID_STORAGE_KEY)
    return UUID_PATTERN.test(persistedClientId || '') ? persistedClientId : UNKNOWN_CORRELATION_ID
  } catch {
    return UNKNOWN_CORRELATION_ID
  }
}

/**
 * 建立 UA-level extra_headers 使用的 Kingnet client metadata Header。
 * cid 用於跨頁面關聯同一瀏覽器 profile；sid 綁定 UA 實例，每次建立 UA 時重新產生，
 * 讓同一頁面內的多次連線嘗試（例如重試時重建 UA）在 log 上可以區分開來。
 * ua 固定為最後一個欄位，接收端應將第一個「;ua=」之後的完整內容視為原始 UA，
 * 不可再用分號切割，因為標準瀏覽器 UA 本身就可能包含分號。
 * 瀏覽器 UA 正常不含控制字元；此處仍移除控制字元，避免偽造額外 SIP Header。
 */
export const createClientMetaHeader = ({ navigatorObject, storageObject, cryptoObject }) => {
  const clientId = getOrCreateClientId({ storageObject, cryptoObject })
  const sessionId = createCorrelationIdOrUnknown(cryptoObject)
  const browserUserAgent = (navigatorObject?.userAgent || '')
    // eslint-disable-next-line no-control-regex -- 刻意移除控制字元，避免偽造額外 SIP Header
    .replace(/[\u0000-\u001F\u007F]+/g, ' ')

  return `${CLIENT_META_HEADER_NAME}: v=${CLIENT_META_SCHEMA_VERSION};cid=${clientId};sid=${sessionId};ua=${browserUserAgent}`
}
